// src/app/components/admin/framework-library-setting.tsx
//
// Platform section of Global Settings: how many framework library templates
// (#139) a Stage 2 generation is given as a starting point. 0 turns the
// lookup off. Backend: app/services/framework_library/config.py.

import { useEffect, useState } from "react";
import { Check, Loader2, RotateCcw } from "lucide-react";

import { frameworkLibraryApi, type FrameworkLibrarySettings } from "@/api/framework-library";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;
const field = {
  fontSize: "var(--text-sm)", height: 32, padding: "0 10px", borderRadius: 8, width: 80,
  border: "1px solid var(--border)", backgroundColor: "var(--background)", color: "var(--foreground)",
} as const;

export function FrameworkLibrarySetting() {
  const [settings, setSettings] = useState<FrameworkLibrarySettings | null>(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const adopt = (s: FrameworkLibrarySettings) => {
    setSettings(s);
    setDraft(String(s.templates_per_generation));
  };

  useEffect(() => {
    frameworkLibraryApi.settings().then(adopt).catch((e) => setErr(String(e?.message || e)));
  }, []);

  async function save(value: number | null) {
    setSaving(true);
    setErr("");
    setSaved(false);
    try {
      adopt(await frameworkLibraryApi.saveSettings(value));
      setSaved(true);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  const parsed = Number(draft);
  const valid = draft.trim() !== "" && Number.isInteger(parsed) && parsed >= 0 && parsed <= (settings?.max ?? 10);
  const changed = settings != null && draft !== String(settings.templates_per_generation);

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Framework library
        </h2>

        {err && (
          <div className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}>
            {err}
          </div>
        )}
        {!settings && !err && (
          <p className="mt-5 inline-flex items-center gap-2" style={muted}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        )}

        {settings && (
          <div className="mt-5 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", minWidth: 220 }}>
                Templates per Stage 2 generation
              </span>
              <input inputMode="numeric" style={field} value={draft} disabled={saving}
                onChange={(e) => { setDraft(e.target.value); setSaved(false); }} />
              <button type="button" disabled={!changed || !valid || saving} onClick={() => save(parsed)}
                className="rounded-lg border px-3 py-2"
                style={{ fontSize: "var(--text-sm)", cursor: changed && valid && !saving ? "pointer" : "default",
                         borderColor: "var(--border)", opacity: changed && valid ? 1 : 0.6 }}>
                {saving ? "Saving…" : "Save"}
              </button>
              {settings.source === "admin" && (
                <button type="button" disabled={saving} onClick={() => save(null)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-2"
                  style={{ ...muted, cursor: saving ? "default" : "pointer", background: "transparent" }}>
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to {settings.default}
                </button>
              )}
              {saved && (
                <span className="inline-flex items-center gap-1" style={{ ...muted, color: "var(--alpha-teal)" }}>
                  <Check className="h-3.5 w-3.5" /> Saved
                </span>
              )}
            </div>
            <span style={muted}>
              {settings.source === "admin" ? "Set here" : "Built-in default"} · a whole number from 0 to {settings.max}
              {!valid && draft.trim() !== "" ? " (not a valid value)" : ""}
            </span>

            <div className="mt-2 rounded-lg px-3 py-3" style={{ backgroundColor: "var(--muted, transparent)" }}>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.6 }}>
                <strong>What this does.</strong> Every framework that is approved becomes an anonymised template in
                the framework library: no client name, competitors, URLs or quoted evidence. When Stage 2 writes a
                framework for a new brand, it is shown this many templates as examples. They come from the same
                category first, then similar categories, then the same vertical, and recommended templates come
                first. The brand's own templates and excluded ones are never shown.
              </p>
              <p className="mt-2" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: 1.6 }}>
                The templates only change where the generation starts. Stage 2 must write its own beliefs (a belief
                copied from a template fails validation), and the validation, battery linter and Smoke calibration
                gates are unchanged. Set it to 0 to stop using the library: every generation then starts blank.
                Templates are still added on every approval. The change applies to the next generation; frameworks
                already generated are not affected.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FrameworkLibrarySetting;
